import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  CreateAction,
  CreateFailureAction,
  CreateSuccessAction,
  DeleteAction,
  DeleteSuccessAction,
  LikeFailureAction,
  LikeSuccessAction,
  MessageActionTypes,
  PinFailureAction,
  PinSuccessAction,
  SaveAction,
  SaveFailureAction,
  SaveSuccessAction,
  SearchAction,
  SearchFailureAction,
  SearchSuccessAction,
} from './message.actions';
import { MessageModel } from '../../models/message.model';
import * as fromRoot from '../../redux/index';
import { MessageService } from '../../services/message.service';

@Injectable()
export class MessageEffects {
  private store = inject<Store<fromRoot.State>>(Store);
  private actions$ = inject(Actions);
  private messageService = inject(MessageService);

  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessageActionTypes.SearchAction),
      map((action: SearchAction) => action.payload),
      switchMap((taskId: string) =>
        this.messageService.findAll(taskId).pipe(
          map(message => new SearchSuccessAction(message)),
          catchError(errors => {
            return of(new SearchFailureAction(errors));
          })
        )
      )
    )
  );

  save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessageActionTypes.SaveAction),
      map((action: SaveAction) => action.payload),
      switchMap((model: MessageModel) =>
        this.messageService.save(model).pipe(
          map(message => new SaveSuccessAction(message)),
          catchError(errors => of(new SaveFailureAction(errors)))
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessageActionTypes.CreateAction),
      map((action: CreateAction) => action.payload),
      switchMap((model: MessageModel) =>
        this.messageService.create(model).pipe(
          map(message => new CreateSuccessAction(message)),
          catchError(errors => of(new CreateFailureAction(errors)))
        )
      )
    )
  );

  like$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessageActionTypes.LikeAction),
      map((action: CreateAction) => action.payload),
      switchMap((model: MessageModel) =>
        this.messageService.like(model).pipe(
          map(message => new LikeSuccessAction(message)),
          catchError(errors => of(new LikeFailureAction(errors)))
        )
      )
    )
  );

  pin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessageActionTypes.PinAction),
      map((action: CreateAction) => action.payload),
      switchMap((model: MessageModel) =>
        this.messageService.pin(model).pipe(
          map(message => new PinSuccessAction(message)),
          catchError(errors => of(new PinFailureAction(errors)))
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessageActionTypes.DeleteAction),
      map((action: DeleteAction) => action.payload),
      switchMap((model: MessageModel) =>
        this.messageService.destroy(model).pipe(
          map(() => new DeleteSuccessAction(model)),
          catchError(errors => of(new CreateFailureAction(errors)))
        )
      )
    )
  );
}
